import { type ReactNode, useState } from "react";
import { Footer, Link, IconFa } from "@cruk/cruk-react-components";
import { faChevronDown, faCookieBite } from "@fortawesome/free-solid-svg-icons";

import { reportFormUrl } from "@fwa/src/services/reportFormService";

import { TopSection } from "@fwa/src/components/TopSection";

import { FWA_BASE_URL } from "@fwa/src/services/apiClient";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";

import styles from "./styles.module.css";

export function Layout({
  children,
  path,
  asPath,
}: {
  children: ReactNode;
  path: string;
  asPath: string;
}) {
  const [brandName] = useBrandContext();
  const fullBleedRoutes = [
    "/",
    "/fundraiser/dashboard",
    "/fundraiser/settings",
    "/page/print/[...slug]",
    "/donate/",
  ];
  const isFullBleed = fullBleedRoutes.includes(path);

  const [showLinks, setShowLinks] = useState(false);

  const userReportUrl = reportFormUrl({
    issueUrl: `${FWA_BASE_URL}${asPath}`,
  });

  const handleClick = () => {
    setShowLinks(!showLinks);
  };

  return (
    <>
      <TopSection />
      <main>
        <div
          className={styles["page-content-wrapper"]}
          id="main"
          data-is-full-bleed={isFullBleed}
        >
          {children}
        </div>
      </main>
      <div className={styles["footer-wrapper"]}>
        <div className={styles["content-max-width"]}>
          <div className="hotjar-survey">{/* Hotjar survey placeholder */}</div>
          <Footer themeName={brandName}>
            <div className={styles["footer-links"]}>
              <Link
                appearance="primary"
                href="https://www.cancerresearchuk.org/about-us/contact-us"
              >
                Contact us
              </Link>
              <Link
                appearance="primary"
                href="https://www.cancerresearchuk.org/get-involved/do-your-own-fundraising/practical-fundraising-help/cruk-giving-page-help/my-page/fees-and-charges"
              >
                Fees and charges
              </Link>

              <Link
                appearance="primary"
                href="https://www.cancerresearchuk.org/privacy-statement"
              >
                Privacy
              </Link>
              <Link
                appearance="primary"
                href="https://www.cancerresearchuk.org/terms-and-conditions/cookies-policy"
              >
                Cookies
              </Link>
              <button
                type="button"
                className={[styles["styled-button"], "component-link"].join(
                  " ",
                )}
                onClick={() => {
                  if (
                    typeof window === "undefined" ||
                    !window?.OneTrust?.ToggleInfoDisplay
                  ) {
                    return;
                  }
                  window.OneTrust?.ToggleInfoDisplay();
                }}
              >
                Cookie settings <IconFa faIcon={faCookieBite} />
              </button>

              <Link
                appearance="primary"
                href="https://www.cancerresearchuk.org/accessibility"
              >
                Accessibility
              </Link>
              <Link
                suppressHydrationWarning
                appearance="primary"
                target="_blank"
                href={userReportUrl}
              >
                Report this page
              </Link>
              <Link
                appearance="primary"
                href="https://www.cancerresearchuk.org/get-involved/do-your-own-fundraising/practical-fundraising-help/cruk-giving-page-help"
              >
                Help
              </Link>
              <div>
                <button
                  type="button"
                  className={[styles["styled-button"], "component-link"].join(
                    " ",
                  )}
                  onClick={handleClick}
                >
                  <span className={styles["terms-content"]}>
                    Terms & Conditions
                    <span
                      className={styles["chevron-wrapper"]}
                      data-is-active={showLinks}
                    >
                      <IconFa faIcon={faChevronDown} size="1em" color="link" />
                    </span>
                  </span>
                </button>
                {showLinks ? (
                  <ul className={styles["ul"]}>
                    <li className={styles["li"]}>
                      <Link
                        appearance="primary"
                        href="https://www.cancerresearchuk.org/terms-and-conditions"
                      >
                        Cancer Research UK
                      </Link>
                    </li>
                    <li className={styles["li"]}>
                      <Link
                        appearance="primary"
                        href="https://www.standuptocancer.org.uk/information/terms-and-conditions"
                      >
                        Stand Up to Cancer
                      </Link>
                    </li>
                  </ul>
                ) : null}
              </div>
            </div>
          </Footer>
        </div>
      </div>
    </>
  );
}

export default Layout;
